<template>
  <div class="card card-custom card-shadowless border mb-5">
    <div class="card-header border-0 py-4">
      <div class="card-title align-items-center">
        <span class="card-label font-weight-bolder text-dark">
          <i class="fa fa-file-word mr-2 text-primary"></i>
          {{ copy.title }}
        </span>
      </div>
      <div class="card-toolbar">
        <button
          type="button"
          class="btn btn-sm btn-light-primary font-weight-bold"
          :disabled="busy"
          @click.prevent="loadStatus"
        >
          <i class="fa fa-sync" :class="{ 'fa-spin': loading }"></i>
          Refresh
        </button>
      </div>
    </div>

    <div class="card-body pt-2 pb-5">
      <p class="text-muted font-size-sm mb-3">
        {{ copy.intro }}
      </p>
      <div v-if="identityLine" class="font-size-sm text-dark mb-4">
        {{ identityLine }}
      </div>

      <ol class="pl-4 mb-0">
        <li class="mb-5">
          <div class="font-weight-bold text-dark mb-1">{{ copy.generateTitle }}</div>
          <div v-if="snapshot" class="font-size-sm text-success mb-2">
            <i class="fa fa-check-circle"></i>
            {{ copy.alreadyGenerated }}
            <span v-if="snapshot.generatedAt"> Generated {{ snapshot.generatedAt }}.</span>
          </div>
          <div v-else-if="!hasFusePolicyNumber" class="font-size-sm text-muted mb-2">
            {{ copy.missingFuse }}
          </div>
          <div v-else class="font-size-sm text-muted mb-2">
            Create the Word schedule from the stamped commercial terms (FUSE-…).
          </div>

          <div class="d-flex flex-wrap align-items-center">
            <button
              v-if="!snapshot"
              type="button"
              class="btn btn-primary font-weight-bold mr-2 mb-2"
              :disabled="busy || !hasFusePolicyNumber"
              @click.prevent="generate(false)"
            >
              <i class="fa fa-file-word" :class="{ 'fa-spin': generating }"></i>
              Generate
            </button>
            <template v-else>
              <button
                type="button"
                class="btn btn-light-primary font-weight-bold mr-2 mb-2"
                :disabled="busy || !activeRenderToken"
                @click.prevent="download"
              >
                <i class="fa fa-download"></i>
                Download
              </button>
              <button
                type="button"
                class="btn btn-light-warning font-weight-bold mb-2"
                :disabled="busy"
                @click.prevent="confirmRegenerate"
              >
                <i class="fa fa-redo" :class="{ 'fa-spin': generating }"></i>
                Regenerate
              </button>
            </template>
          </div>
          <div v-if="snapshot" class="text-muted font-size-sm mt-1">
            {{ copy.regenerateHelp }}
          </div>
        </li>

        <li>
          <div class="font-weight-bold text-dark mb-1">{{ copy.attachTitle }}</div>

          <div v-if="!hasInsightDraft" class="border rounded bg-light p-4 mb-2">
            <div class="font-size-sm text-dark mb-3">
              Attach is locked until an INSIGHT draft exists. Use
              <strong>Generate</strong> above first if you only need the Word file, then
              <strong>Create draft</strong> in Post to INSIGHT below before attaching.
            </div>
            <button
              type="button"
              class="btn btn-primary font-weight-bold text-uppercase"
              @click.prevent="$emit('focus-posting-rail')"
            >
              Create draft in Posting steps
            </button>
          </div>

          <template v-else>
            <div class="font-size-sm text-muted mb-3">
              Confirm the INSIGHT transaction ID twice. The server verifies that it belongs to this
              Zoho policy before uploading the schedule.
            </div>

            <div v-if="attachedTransactionId" class="text-success font-size-sm mb-3">
              <i class="fa fa-check-circle"></i>
              Attached to INSIGHT transaction {{ attachedTransactionId }}.
            </div>

            <div class="row">
              <div class="col-md-4 mb-2">
                <label class="font-weight-bold font-size-sm">INSIGHT transaction ID</label>
                <input
                  v-model="attachTxnId"
                  type="text"
                  inputmode="numeric"
                  class="form-control form-control-sm"
                  autocomplete="off"
                  :disabled="busy"
                />
              </div>
              <div class="col-md-4 mb-2">
                <label class="font-weight-bold font-size-sm">Type the ID again</label>
                <input
                  v-model="attachTxnIdTyped"
                  type="text"
                  inputmode="numeric"
                  class="form-control form-control-sm"
                  autocomplete="off"
                  :disabled="busy"
                />
              </div>
            </div>

            <div v-if="!identityReady" class="text-muted font-size-sm mb-2">
              {{ copy.missingIdentity }}
            </div>
            <div v-else-if="!snapshot" class="text-muted font-size-sm mb-2">
              Generate the schedule first.
            </div>
            <div v-else-if="idsEntered && !idsMatch" class="text-danger font-size-sm mb-2">
              {{ copy.attachMismatch }}
            </div>

            <button
              type="button"
              class="btn btn-success font-weight-bold"
              :disabled="busy || !canAttach"
              @click.prevent="attach"
            >
              <i class="fa fa-paperclip" :class="{ 'fa-spin': attaching }"></i>
              Attach schedule
            </button>
          </template>
        </li>
      </ol>

      <b-alert v-if="error" variant="warning" show class="mt-4 mb-0">
        {{ error }}
      </b-alert>
      <b-alert v-else-if="info" variant="success" show class="mt-4 mb-0">
        {{ info }}
      </b-alert>
    </div>
  </div>
</template>

<script>
import {
  generateScheduleDocument,
  scheduleDocumentStatus,
  attachSchedulePosted,
  downloadGeneratedSchedule
} from "@/services/insight-post-service";
import {
  SCHEDULE_DOCUMENTS_TITLE,
  SCHEDULE_DOCUMENTS_GENERATE_TITLE,
  SCHEDULE_DOCUMENTS_ATTACH_TITLE,
  SCHEDULE_DOCUMENTS_INTRO,
  SCHEDULE_DOCUMENTS_MISSING_IDENTITY,
  SCHEDULE_DOCUMENTS_MISSING_FUSE,
  SCHEDULE_DOCUMENTS_ALREADY_GENERATED,
  SCHEDULE_DOCUMENTS_REGENERATE_HELP,
  SCHEDULE_DOCUMENTS_ATTACH_MISMATCH,
  SCHEDULE_DOCUMENTS_ATTACH_SUCCESS
} from "@/copy/schedule-documents-copy";

export default {
  name: "ScheduleDocumentsRail",

  props: {
    ledgerRowId: {
      type: String,
      required: true
    },
    policyNumber: {
      type: String,
      default: ""
    },
    workflowName: {
      type: String,
      default: ""
    },
    expectedSnapshotHash: {
      type: String,
      default: ""
    },
    workflowTransactionType: {
      type: String,
      default: ""
    },
    identityReady: {
      type: Boolean,
      default: false
    },
    /** True when Posting rail already has an INSIGHT draft/txn for this ledger row. */
    hasInsightDraft: {
      type: Boolean,
      default: false
    },
    /** Prefill attach fields from posting status when a draft exists. */
    draftInsightTransactionId: {
      type: String,
      default: ""
    }
  },

  data: function() {
    return {
      loading: false,
      generating: false,
      attaching: false,
      snapshot: null,
      generation: null,
      attachTxnId: "",
      attachTxnIdTyped: "",
      error: null,
      info: null,
      copy: {
        title: SCHEDULE_DOCUMENTS_TITLE,
        generateTitle: SCHEDULE_DOCUMENTS_GENERATE_TITLE,
        attachTitle: SCHEDULE_DOCUMENTS_ATTACH_TITLE,
        intro: SCHEDULE_DOCUMENTS_INTRO,
        missingIdentity: SCHEDULE_DOCUMENTS_MISSING_IDENTITY,
        missingFuse: SCHEDULE_DOCUMENTS_MISSING_FUSE,
        alreadyGenerated: SCHEDULE_DOCUMENTS_ALREADY_GENERATED,
        regenerateHelp: SCHEDULE_DOCUMENTS_REGENERATE_HELP,
        attachMismatch: SCHEDULE_DOCUMENTS_ATTACH_MISMATCH
      }
    };
  },

  computed: {
    busy: function() {
      return this.loading || this.generating || this.attaching;
    },
    hasFusePolicyNumber: function() {
      return /^FUSE-\d{1,12}$/i.test(String(this.policyNumber || "").trim());
    },
    identityLine: function() {
      const fuse = String(this.policyNumber || "").trim();
      const workflow = String(this.workflowName || "").trim();
      if (fuse && workflow && fuse !== workflow) {
        return `Workflow ${workflow} · Policy ${fuse}`;
      }
      if (fuse) return `Policy ${fuse}`;
      if (workflow) return `Workflow ${workflow}`;
      return "";
    },
    activeRenderToken: function() {
      return (
        (this.generation && this.generation.renderToken) ||
        (this.snapshot && this.snapshot.renderToken) ||
        ""
      );
    },
    activeSha256: function() {
      return (
        (this.generation && this.generation.sha256) ||
        (this.snapshot && this.snapshot.docxSha256) ||
        ""
      );
    },
    idsEntered: function() {
      return !!(
        String(this.attachTxnId || "").trim() &&
        String(this.attachTxnIdTyped || "").trim()
      );
    },
    idsMatch: function() {
      return (
        String(this.attachTxnId || "").trim() ===
        String(this.attachTxnIdTyped || "").trim()
      );
    },
    validTransactionId: function() {
      return /^[1-9]\d*$/.test(String(this.attachTxnId || "").trim());
    },
    canAttach: function() {
      return (
        this.hasInsightDraft &&
        this.identityReady &&
        !!this.snapshot &&
        !!this.activeRenderToken &&
        this.idsEntered &&
        this.idsMatch &&
        this.validTransactionId
      );
    },
    attachedTransactionId: function() {
      return this.snapshot && this.snapshot.insightTransactionId
        ? this.snapshot.insightTransactionId
        : null;
    },
    downloadFileName: function() {
      const title =
        (this.snapshot && this.snapshot.documentTitle) ||
        (this.generation && this.generation.documentTitle) ||
        "schedule";
      return String(title).replace(/\.docx$/i, "") + ".docx";
    }
  },

  mounted: function() {
    this.loadStatus();
  },

  watch: {
    ledgerRowId: function() {
      this.snapshot = null;
      this.generation = null;
      this.attachTxnId = "";
      this.attachTxnIdTyped = "";
      this.loadStatus();
    },
    draftInsightTransactionId: {
      immediate: true,
      handler: function(value) {
        const id = String(value || "").trim();
        if (/^[1-9]\d*$/.test(id)) {
          this.attachTxnId = id;
          this.attachTxnIdTyped = id;
        }
      }
    }
  },

  methods: {
    loadStatus: function() {
      const id = String(this.ledgerRowId || "").trim();
      if (!id) return;
      this.loading = true;
      this.error = null;
      return scheduleDocumentStatus(id)
        .then(result => {
          this.loading = false;
          if (!result || result.ok === false) {
            this.error =
              (result && (result.error || result.detail)) ||
              "Unable to read schedule document status.";
            return null;
          }
          this.snapshot = result.snapshot || null;
          return this.snapshot;
        })
        .catch(() => {
          this.loading = false;
          this.error = "Unable to read schedule document status.";
          return null;
        });
    },

    confirmRegenerate: function() {
      if (this.busy || !window.confirm(SCHEDULE_DOCUMENTS_REGENERATE_HELP)) return;
      this.generate(true);
    },

    generate: function(forceRegenerate) {
      const id = String(this.ledgerRowId || "").trim();
      if (!id || this.busy) return;
      if (!this.hasFusePolicyNumber) {
        this.error = SCHEDULE_DOCUMENTS_MISSING_FUSE;
        return;
      }
      this.generating = true;
      this.error = null;
      this.info = null;
      return generateScheduleDocument(id, {
        forceRegenerate: forceRegenerate === true,
        policyNumber: this.policyNumber || undefined,
        expectedSnapshotHash: this.expectedSnapshotHash || undefined,
        workflowTransactionType: this.workflowTransactionType || undefined
      })
        .then(result => {
          this.generating = false;
          if (!result || result.ok === false) {
            this.error =
              (result && (result.error || result.detail)) ||
              "Schedule generation was refused.";
            return null;
          }
          this.generation = result;
          this.snapshot = result.snapshot || result;
          this.info = "Schedule generated. Download and review it before attaching.";
          return result;
        })
        .catch(err => {
          this.generating = false;
          const data = err && err.response && err.response.data;
          const detail =
            data && (data.error || data.message || data.result);
          this.error = detail
            ? String(detail)
            : "Unable to generate the schedule right now.";
          return null;
        });
    },

    download: function() {
      const id = String(this.ledgerRowId || "").trim();
      if (!id || !this.activeRenderToken) return;
      this.error = null;
      return downloadGeneratedSchedule(id, this.activeRenderToken)
        .then(blob => {
          if (!blob) return;
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = this.downloadFileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          this.error =
            "The generated schedule could not be downloaded. Regenerate it if the token has expired.";
        });
    },

    attach: function() {
      const id = String(this.ledgerRowId || "").trim();
      if (!id || this.busy || !this.canAttach) return;
      if (!this.idsMatch) {
        this.error = SCHEDULE_DOCUMENTS_ATTACH_MISMATCH;
        return;
      }
      this.attaching = true;
      this.error = null;
      this.info = null;
      return attachSchedulePosted(id, {
        insightTransactionId: String(this.attachTxnId).trim(),
        insightTransactionIdTyped: String(this.attachTxnIdTyped).trim(),
        renderToken: this.activeRenderToken,
        sha256: this.activeSha256 || undefined
      })
        .then(result => {
          this.attaching = false;
          if (!result || result.ok === false) {
            this.error =
              result && result.code === "transaction_id_mismatch"
                ? SCHEDULE_DOCUMENTS_ATTACH_MISMATCH
                : (result && (result.error || result.detail)) ||
                  "Schedule attachment was refused.";
            return null;
          }
          this.info = SCHEDULE_DOCUMENTS_ATTACH_SUCCESS;
          return this.loadStatus();
        })
        .catch(() => {
          this.attaching = false;
          this.error = "Unable to attach the schedule right now.";
          return null;
        });
    }
  }
};
</script>
